import React, { useState, useEffect, useRef } from "react";
import { socket } from "../socket";
import { PlayerAvatar } from "./PlayerIdentity";
import Heart from "./Heart";
import { t } from "../i18n";
import { useSounds } from "../sound";
import { alertDialog } from "../dialog";

const ALPHABET = "abcdefghijklmnopqrstuvwxyz".split("");
// La frappe part sur le réseau au fil des touches : on la limite à ~10 envois
// par seconde, largement assez pour que le mot paraisse s'écrire en direct.
const TYPING_INTERVAL = 100;
// Rayon du cercle de joueurs, en pourcentage du côté de l'arène.
const RING_RADIUS = 38;

// Met en gras la syllabe imposée à l'intérieur du mot.
function WithSyllable({ word, syllable }) {
  const i = syllable ? word.toLowerCase().indexOf(syllable.toLowerCase()) : -1;
  if (i < 0) return <>{word}</>;
  return (
    <>
      {word.slice(0, i)}
      <b>{word.slice(i, i + syllable.length)}</b>
      {word.slice(i + syllable.length)}
    </>
  );
}

// Mode Bombe : les joueurs en cercle, la bombe au centre, une flèche qui
// désigne celui qui a la main. Le minuteur est volontairement caché — le
// serveur ne l'envoie jamais, la tension vient de là.
function BombGame({ gameId, player }) {
  const [syllable, setSyllable] = useState("");
  const [currentId, setCurrentId] = useState(null);
  const [players, setPlayers]   = useState([]);
  const [text, setText]         = useState("");
  const [remoteText, setRemote] = useState("");
  const [rejected, setRejected] = useState(null);
  const [blast, setBlast]       = useState(null);
  const [bonus, setBonus]       = useState(null);

  const myId = player?.playerId;
  const myTurn = currentId === myId;
  const { playPop, playFail } = useSounds();
  const inputRef = useRef(null);
  const sentAt = useRef(0);
  const pending = useRef(null);

  useEffect(() => {
    socket.on("boState", (s) => {
      setSyllable(s.syllable || ""); setCurrentId(s.currentId);
      setPlayers(s.players); setRemote(s.typing || "");
    });
    socket.on("boTurn", ({ syllable, currentId, players }) => {
      setSyllable(syllable); setCurrentId(currentId); setPlayers(players);
      setText(""); setRemote(""); setRejected(null); setBlast(null);
    });
    socket.on("boTyping", ({ text }) => setRemote(text));
    socket.on("boAccepted", ({ playerId, bonus, players }) => {
      playPop();
      setPlayers(players);
      if (bonus) setBonus(playerId);
    });
    socket.on("boRejected", ({ reason }) => { playFail(); setRejected(reason); });
    socket.on("boExplosion", ({ playerId, eliminated, players }) => {
      playFail();
      setPlayers(players); setBlast({ playerId, eliminated });
      setText(""); setRemote("");
    });
    socket.on("boError", ({ error }) => alertDialog(error));

    socket.emit("boGetState", { gameId });
    return () => {
      ["boState", "boTurn", "boTyping", "boAccepted", "boRejected", "boExplosion", "boError"]
        .forEach(e => socket.off(e));
    };
  }, [gameId, playPop, playFail]);

  // Le champ est invisible mais bien présent : c'est lui qui capte le clavier,
  // et sur mobile c'est la seule façon de faire apparaître le clavier virtuel.
  // Il reprend le focus à chaque tour. Un téléphone n'ouvre le clavier que sur
  // un geste du joueur : le premier toucher le fait monter, et comme le champ
  // n'est jamais désactivé, il reste ouvert pour les tours suivants.
  useEffect(() => { if (myTurn) inputRef.current?.focus(); }, [myTurn, currentId]);

  useEffect(() => {
    if (!bonus) return;
    const timer = setTimeout(() => setBonus(null), 2500);
    return () => clearTimeout(timer);
  }, [bonus]);

  const sendTyping = (value) => {
    const now = Date.now();
    clearTimeout(pending.current);
    if (now - sentAt.current >= TYPING_INTERVAL) {
      sentAt.current = now;
      socket.emit("boTyping", { gameId, text: value });
    } else {
      // Dernière frappe d'une rafale : sans ce rattrapage, les autres restent
      // sur un mot tronqué jusqu'à la touche suivante.
      pending.current = setTimeout(() => {
        sentAt.current = Date.now();
        socket.emit("boTyping", { gameId, text: value });
      }, TYPING_INTERVAL);
    }
  };

  const onChange = (e) => {
    // Hors de son tour, la frappe ne compte pas : le champ reste actif pour
    // que le clavier du téléphone ne se referme pas entre deux tours.
    if (!myTurn || blast) { setText(""); return; }
    const value = e.target.value.slice(0, 40);
    setText(value);
    setRejected(null);
    if (myTurn) sendTyping(value);
  };

  const onKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (!myTurn || blast) return;
    // On lit le champ plutôt que l'état React : en tapant vite, la validation
    // peut tomber dans la même frame que la dernière lettre, et l'état serait
    // alors en retard d'un rendu.
    const word = e.target.value.trim();
    if (word) socket.emit("boSubmit", { gameId, word });
  };

  const me = players.find(p => p.playerId === myId);
  const myLetters = new Set(me?.letters || []);
  const current = players.find(p => p.playerId === currentId);
  const currentIdx = players.findIndex(p => p.playerId === currentId);
  const shown = myTurn ? text : remoteText;

  if (!syllable) {
    return <div className="BombGame"><p className="bo-waiting">{t("bombe.waiting")}</p></div>;
  }

  // Angle de la flèche : elle pointe la place du joueur qui a la main.
  const arrowDeg = currentIdx >= 0 && players.length
    ? (360 / players.length) * currentIdx - 90
    : -90;

  return (
    <div className={`BombGame${me && me.eliminated ? " is-out" : ""}`}
      onClick={() => { if (!me?.eliminated) inputRef.current?.focus(); }}>

      {/* Invisible, mais focusable : c'est le clavier du joueur. */}
      <input ref={inputRef} className="bo-ghost" type="text" value={text}
        onChange={onChange} onKeyDown={onKeyDown}
        autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false"
        enterKeyHint="send" aria-label={t("bombe.yourTurn")} />

      <div className="bo-arena">
        {/* La flèche est fille de l'arène, pas du centre : sa longueur se mesure
            au cercle des joueurs, pas au diamètre de la bombe. */}
        <div className={`bo-arrow${blast ? " hidden" : ""}`} style={{ "--deg": `${arrowDeg}deg` }} />
        <div className="bo-center">
          <div className={`bo-bomb${blast ? " blown" : ""}`}>
            <span className="bo-syllable">{syllable.toUpperCase()}</span>
          </div>
        </div>

        {players.map((p, i) => {
          const deg = (360 / players.length) * i - 90;
          const rad = (deg * Math.PI) / 180;
          const active = p.playerId === currentId;
          return (
            <div key={p.playerId}
              className={`bo-seat${active ? " active" : ""}${p.eliminated ? " out" : ""}`
                + `${p.playerId === myId ? " me" : ""}${p.offline ? " offline" : ""}`
                + `${bonus === p.playerId ? " bonus" : ""}`}
              style={{
                left: `${50 + RING_RADIUS * Math.cos(rad)}%`,
                top:  `${50 + RING_RADIUS * Math.sin(rad)}%`,
              }}>
              <span className="bo-hearts" title={p.pseudo}>
                {/* Les cœurs perdus restent visibles, éteints : on lit l'état
                    de chacun sans compter. */}
                {Array.from({ length: Math.max(p.lives, p.baseLives || p.lives) }, (_, k) => (
                  <Heart key={k} className={`bo-heart${k < p.lives ? "" : " spent"}`} />
                ))}
              </span>
              <PlayerAvatar player={p} size={52} />
              {/* Le mot en cours pour celui qui joue, le dernier trouvé sinon */}
              <span className={`bo-seat-word${active && rejected ? " rejected" : ""}`}>
                {active
                  ? (shown ? <WithSyllable word={shown.toUpperCase()} syllable={syllable.toUpperCase()} /> : <span className="bo-caret" />)
                  : p.lastWord
                    ? <WithSyllable word={p.lastWord.word.toUpperCase()} syllable={p.lastWord.syllable.toUpperCase()} />
                    : null}
              </span>
            </div>
          );
        })}
      </div>

      <p className="bo-feedback">
        {blast
          ? (blast.eliminated
              ? t("bombe.knockedOut", { pseudo: players.find(p => p.playerId === blast.playerId)?.pseudo || "?" })
              : t("bombe.exploded", { pseudo: players.find(p => p.playerId === blast.playerId)?.pseudo || "?" }))
          : rejected ? t(`bombe.reject.${rejected}`)
          : myTurn ? t("bombe.yourTurn")
          : t("bombe.turnOf", { pseudo: current?.pseudo || "…" })}
      </p>

      <div className="bo-alphabet" title={t("bombe.alphabetHint")}>
        {ALPHABET.map(c => (
          <span key={c} className={`bo-letter${myLetters.has(c) ? " got" : ""}`}>{c.toUpperCase()}</span>
        ))}
      </div>
    </div>
  );
}

export default BombGame;
